import { CustomFieldEntityEnum } from '@monetize/types/app';
import { useEffect, useMemo, useState } from 'react';
import {
  MAccordion,
  MBox,
  MFlex,
  MPageLoader,
} from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import { useCustomFields } from '../../../../hooks/useCustomFields';
import { useDocumentHead } from '../../../../services/documentHead';
import { QuoteBanners } from '../../Quote/quoteBanners/QuoteBanners';
import { useQuoteContext } from '../../Quote/quoteContext';
import { QuoteBasic } from './QuoteBasic';
import { QuoteCustomizingPdf } from './QuoteCustomizingPdf';
import { QuoteInternalBilling } from './QuoteInternalBilling';
import { QuoteInternalCustomFields } from './QuoteInternalCustomFields';
import { QuoteInternalExtraInfo } from './QuoteInternalExtraInfo';
import { QuoteInternalOfferings } from './QuoteInternalOfferings';

interface QuotePageInternalProps {
  customizePdfFormDisabled: boolean;
  headerHeight?: number;
}
export const QuotePageInternal = ({
  customizePdfFormDisabled,
  headerHeight = 0,
}: QuotePageInternalProps) => {
  const { quoteData, handleSubmitButton } = useQuoteContext();
  const { quote } = quoteData;
  const { setDocTitle } = useDocumentHead();
  // v3's Accordion.Root tracks expanded items by string `value`, not a
  // numeric index - each section's Accordion.Item `value` is its
  // stringified render position.
  const [openAccordionValues, setOpenAccordionValues] = useState(['0']);

  // The custom fields accordion is hidden when no quote custom fields exist, so
  // downstream accordion positions shift accordingly
  const { customFieldList } = useCustomFields(CustomFieldEntityEnum.QUOTE);
  const hasCustomFields = customFieldList.length > 0;

  const accordionItemCount = hasCustomFields ? 5 : 4;
  const pdfAccordionValue = String(accordionItemCount - 1);
  const allAccordionValues = useMemo(
    () =>
      Array.from({ length: accordionItemCount }, (_, index) => String(index)),
    [accordionItemCount],
  );

  const isAccordionExpanded =
    openAccordionValues.length === allAccordionValues.length;

  const onToggleAccordion = () => {
    if (isAccordionExpanded) {
      setOpenAccordionValues([]);
    } else {
      setOpenAccordionValues(allAccordionValues);
    }
  };

  useEffect(() => {
    setDocTitle(
      quote?.id &&
        `Edit Quote${quote.description ? ` - ${quote.description}` : ''}`,
    );
  }, [quote?.description, quote?.id, setDocTitle]);

  if (!quote) {
    return <MPageLoader />;
  }

  return (
    <form onSubmit={handleSubmitButton} style={{ width: '100%' }}>
      <QuoteBanners />

      <MAccordion
        w="100%"
        my={4}
        collapsible
        variant="outline"
        defaultValue={['0']}
      >
        <QuoteBasic value="0" />
      </MAccordion>

      <MFlex alignItems="center" mb="1" justifyContent="flex-start">
        <ExpandCollapseButton
          isExpanded={isAccordionExpanded}
          onChange={onToggleAccordion}
        />
      </MFlex>

      <MBox
        overflow="hidden"
        marginBottom="100px" // Give some space below
      >
        <MAccordion
          multiple
          p={1}
          variant="bottomBorder"
          value={openAccordionValues}
          onValueChange={({ value }) => setOpenAccordionValues(value)}
        >
          <QuoteInternalOfferings value="0" />
          {hasCustomFields && <QuoteInternalCustomFields value="1" />}
          <QuoteInternalExtraInfo value={hasCustomFields ? '2' : '1'} />
          <QuoteInternalBilling value={hasCustomFields ? '3' : '2'} />
          <QuoteCustomizingPdf
            value={pdfAccordionValue}
            customizePdfFormDisabled={customizePdfFormDisabled}
            headerHeight={headerHeight}
          />
        </MAccordion>
      </MBox>
    </form>
  );
};
